import HomeyLib from 'homey-lib/webpack/index.js';
import orderby from 'lodash.orderby';
import { useEffect, useMemo, useState } from 'react';
import { useLocale } from 'react-aria';

import { getIsDeviceQuickActionStateOnly } from '../../../lib/deviceQuickAction.ts';
import { round } from '../../../lib/math';

import { devicesStore } from '../../../store/devices/DevicesStore.ts';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useAppsById } from '../../../store/apps/useApps.ts';
import { useDevicesById } from '../../../store/devices/useDevices.ts';
import { useApi } from '../../../store/SelectedHomeyStore.hooks.ts';
import { useZonesTree } from '../../../store/zones/useZones.ts';

import { space } from '../../../components/common/space';

import { DeviceColumnHead } from './DeviceColumnHead';
import { order } from './DeviceColumnHead';
import { DeviceRow } from './DeviceRow';
import { Table } from './Table';

const deviceClasses = HomeyLib.Device.getClasses();

const orderType = {
  NAME: 'name',
  ZONENAME: 'zoneName',
  TYPE: 'type',
  ENERGY: 'energy',
  APP: 'app',
  STATUS: 'status',
  BATTERYSTATUS: 'batteryStatus',
  BATTERIES: 'batteries',
  LAST_SEEN_AT: 'lastSeenAt',
};

const orderTypes = {
  [orderType.NAME]: (deviceRow) => deviceRow.name,
  [orderType.ZONENAME]: (deviceRow) => deviceRow.zoneName,
  [orderType.TYPE]: (deviceRow) => deviceRow.class,
  [orderType.ENERGY]: (deviceRow) => deviceRow.energy,
  [orderType.APP]: (deviceRow) => deviceRow.app,
  [orderType.STATUS]: (deviceRow) => deviceRow.status,
  [orderType.BATTERYSTATUS]: (deviceRow) => deviceRow.batteryStatus?.order,
  [orderType.BATTERIES]: (deviceRow) => deviceRow.batteries,
  [orderType.LAST_SEEN_AT]: (deviceRow) => deviceRow.lastSeenAt,
};

export function DevicesTable(props) {
  const { i18n } = useI18n();

  const [orderBy, setOrderBy] = useState(orderType.NAME);
  const [hoverIndex, setHoverIndex] = useState(false);
  const [orderDirection, setOrderDirection] = useState(order.ASC);

  const { api } = useApi();
  const devices = useDevicesById();
  const zones = useZonesTree();
  const apps = useAppsById();

  const supportsLastSeenAt = !(api?.homey.platform === 'local' && api?.homey.platformVersion === 1);

  const { locale } = useLocale();

  useRefresh({ store: devicesStore, intervalMs: 10000 });

  function sort(type) {
    if (type !== orderBy) {
      setOrderBy(type);
    } else {
      if (orderDirection === order.DESC) {
        setOrderDirection(order.ASC);
      } else {
        setOrderDirection(order.DESC);
      }
    }
  }

  function head(name, sort) {
    return { name: name, sort: sort };
  }

  const devicesTableHeader = [
    false,
    head(i18n.messageFormatter('devicesTable.thead.name'), orderType.NAME),
    head(i18n.messageFormatter('devicesTable.thead.zone'), orderType.ZONENAME),
    head(i18n.messageFormatter('devicesTable.thead.type'), orderType.TYPE),
    head(i18n.messageFormatter('devicesTable.thead.app'), orderType.APP),
    head(i18n.messageFormatter('devicesTable.thead.state'), orderType.STATUS),
    head(i18n.messageFormatter('devicesTable.thead.batteryStatus'), orderType.BATTERYSTATUS),
    head(i18n.messageFormatter('devicesTable.thead.batteries'), orderType.BATTERIES),
    head(i18n.messageFormatter('devicesTable.thead.energy'), orderType.ENERGY),
    ...(supportsLastSeenAt
      ? [head(i18n.messageFormatter('devicesTable.thead.lastSeenAt'), orderType.LAST_SEEN_AT)]
      : []),
    false, // Empty spacer column to fill up remaining space.
    false,
  ];

  // Sort and filter data for Devices.
  const devicesRowData = useMemo(() => {
    if (devices.byId == null || zones.byId == null || apps.byId == null) {
      return null;
    }

    // Prefill device rows with correct data.
    const devicesRowData = [];

    for (const [, device] of Object.entries(devices.byId)) {
      if (device.hidden === true) continue;
      const deviceClass = device.virtualClass ?? device.class;
      const deviceRow = {
        id: device.id,
        iconId: device.iconObj?.id,
        name: device.name,
        zone: device.zone,
        available: device.available,
        ready: device.ready,
        zoneName: zones.byId?.[device.zone]?.name,
        class: deviceClasses[deviceClass] ? deviceClasses[deviceClass].title[locale] : deviceClass,
        app: getAppNameByDriverId(device.driverId, apps.byId),
        status: getDeviceStatus(device),
        batteryStatus: getBatteryStatus(device),
        batteries: getBatteries(device),
        energy: getEnergyUsage(device),
        lastSeenAt: device.lastSeenAt ? new Date(device.lastSeenAt) : null,
      };
      devicesRowData.push(deviceRow);
    }

    return devicesRowData;
  }, [devices.byId, apps.byId, zones.byId, locale]);

  const orderedDevices = useMemo(() => {
    if (devicesRowData == null) {
      return null;
    }

    const selectedZoneDescendants = zones.tree?.descendants?.[props.selectedZoneId] ?? new Set();

    return orderby(
      devicesRowData,
      [
        (device) => {
          // Show empty items from given column always at the bottom.
          return device[orderBy] === null;
        },

        orderTypes[orderBy],
      ],
      [order.ASC, orderDirection],
    ).filter((device) => {
      // When Home show all
      if (props.selectedZoneId === null) {
        return true;
      }

      // When a device in the current zone or in zones below current zone.
      return device.zone === props.selectedZoneId || selectedZoneDescendants.has(device.zone);
    });
  }, [devicesRowData, orderBy, orderDirection, zones.tree, props.selectedZoneId]);

  return (
    <Table.table>
      <colgroup>
        {devicesTableHeader.map((head, index) => {
          return <Table.col key={index} isHover={index === hoverIndex} />;
        })}
      </colgroup>

      <Table.thead>
        <Table.tr>
          {devicesTableHeader.map((head, index) => {
            // Empty header.
            if (head === false) {
              return <DeviceColumnHead key={index}>{space}</DeviceColumnHead>;
            }

            // Header which is sortable.
            if (head.sort !== false) {
              return (
                <DeviceColumnHead
                  key={index}
                  sortable
                  sort={head.sort}
                  orderDirection={orderDirection}
                  orderBy={orderBy}
                  onClick={() => sort(head.sort)}
                  onMouseOver={() => setHoverIndex(index)}
                  onMouseOut={() => setHoverIndex(false)}
                >
                  {' '}
                  {head.name}
                </DeviceColumnHead>
              );
            }

            // Header with only name.
            return <DeviceColumnHead key={index}>{head.name}</DeviceColumnHead>;
          })}
        </Table.tr>
      </Table.thead>

      <Table.tbody>
        {orderedDevices ? (
          orderedDevices.map((device) => {
            return (
              <DeviceRow
                key={device.id}
                deviceId={device.id}
                deviceProps={device}
                isSelected={props.selectedDeviceId === device.id}
                onControlsClick={props.onControlsClick}
                supportsLastSeenAt={supportsLastSeenAt}
              />
            );
          })
        ) : (
          <Table.tr>
            <Table.td colSpan={devicesTableHeader.length}>
              {i18n.messageFormatter('devicesTable.status.loading')}
            </Table.td>
          </Table.tr>
        )}

        {orderedDevices?.length === 0 && (
          <Table.tr>
            <Table.td colSpan={devicesTableHeader.length}>
              {i18n.messageFormatter('devicesTable.status.noDevices')}
            </Table.td>
          </Table.tr>
        )}
      </Table.tbody>
    </Table.table>
  );
}

function useRefresh({ store, intervalMs }) {
  const refresh = store.refresh;

  useEffect(() => {
    let intervalId = null;

    if (store.isLoading === false) {
      intervalId = setInterval(() => {
        refresh();
      }, intervalMs);
    }

    return function () {
      clearInterval(intervalId);
    };
  }, [store.isLoading, refresh, intervalMs]);
}

/**
 * Get Battery Status
 * @param device
 * @returns {Object|null}
 *
 * todo - implement Device.makeCapabilityInstance for live updates
 */
export function getBatteryStatus(device) {
  if (
    !device.energyObj ||
    !device.capabilitiesObj ||
    (!device.energyObj.batteries &&
      !device.capabilitiesObj.alarm_battery &&
      !device.capabilitiesObj.measure_battery)
  ) {
    return null;
  }

  // gray battery settings are default
  let percentage = null;
  let text = '';
  let order = 0;

  // if we know a percentage show that
  if (device.capabilitiesObj.measure_battery) {
    if (device.capabilitiesObj.measure_battery.value) {
      percentage = device.capabilitiesObj.measure_battery.value;
      text = device.capabilitiesObj.measure_battery.value + '%';
      order = device.capabilitiesObj.measure_battery.value;
    }
  }
  // if we know only if there is an alarm battery, show that
  else if (device.capabilitiesObj.alarm_battery) {
    percentage = 10;
    text = 'LOW';
    order = 10;

    if (device.capabilitiesObj.alarm_battery.value === false) {
      percentage = 100;
      text = 'OK';
      order = 100;
    }
  }

  return { percentage: percentage, text: text, order: order };
}

/**
 * Get Batteries
 * @param device
 * @returns {string|null}
 */
export function getBatteries(device) {
  if (!device.energyObj || !device.energyObj.batteries) {
    return null;
  }

  return `${device.energyObj.batteries.length}x ${device.energyObj.batteries[0]}`;
}

/**
 *
 * @param device
 * @returns {JSX.Element|string}
 * todo what happens if we have multiple device statuses
 */
export function getDeviceStatus(device) {
  if (device.available === false || device.ready === false) {
    return null;
  }

  const quickActionOverride = device.ui?.quickActionOverride ?? null;
  let quickAction = device.ui?.quickAction ?? null;

  if (quickActionOverride === '.none') {
    return null;
  }

  if (
    typeof quickActionOverride === 'string' &&
    device.capabilitiesObj?.[quickActionOverride] != null
  ) {
    quickAction = quickActionOverride;
  }

  if (quickAction == null) {
    return null;
  }

  const capability = device.capabilitiesObj?.[quickAction];

  if (capability == null) {
    return null;
  }

  if (
    getIsDeviceQuickActionStateOnly({
      capabilityObj: capability,
      device,
    }) === true
  ) {
    return capability.value ?? false;
  }

  if (capability.getable !== true || capability.setable !== true) {
    return null;
  }

  return capability.value ?? true;
}

/**
 * Get App Name By Key
 * @description function to split fullKey and give back the app name
 * @param driverId
 * @param appsById
 * @returns {string|null}
 */
export function getAppNameByDriverId(driverId, appsById) {
  let appId = null;

  if (driverId.startsWith('homey:app:')) {
    appId = driverId.split(':')[2];
  }

  if (appsById == null || appsById[appId] == null) {
    return null;
  }

  return appsById[appId]?.name;
}

/**
 * Get Energy Usage
 * @param device
 * @returns {string|null}
 */
export function getEnergyUsage(device) {
  if (device.energyObj?.W == null) {
    return null;
  }

  return round(device.energyObj.W, 0);
}
